// Date utility functions

/**
 * Safely formats a date value that could be a Firestore Timestamp, Date object, or string
 */
export function formatDate(dateValue: any, options?: Intl.DateTimeFormatOptions, t?: (key: string) => string): string {
  if (!dateValue || dateValue === null || dateValue === undefined) {
    return t?.('common.never') || 'Never'
  }

  try {
    let date: Date

    // Handle Firestore Timestamp
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      date = dateValue.toDate()
    }
    // Handle Date object
    else if (dateValue instanceof Date) {
      date = dateValue
    }
    // Handle string dates
    else if (typeof dateValue === 'string') {
      date = new Date(dateValue)
    }
    // Handle milliseconds timestamp
    else if (typeof dateValue === 'number') {
      date = new Date(dateValue)
    }
    else {
      console.warn('Unknown date format:', dateValue)
      return t?.('common.invalidDate') || 'Invalid date'
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date value:', dateValue)
      return t?.('common.invalidDate') || 'Invalid date'
    }

    // Format the date
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }

    return date.toLocaleDateString(undefined, options || defaultOptions)
  } catch (error) {
    console.error('Error formatting date:', error, dateValue)
    return t?.('common.invalidDate') || 'Invalid date'
  }
}

/**
 * Formats a date with time included
 */
export function formatDateTime(dateValue: any, t?: (key: string) => string): string {
  return formatDate(dateValue, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }, t)
}

/**
 * Formats a date as a relative time (e.g., "2 days ago")
 */
export function formatRelativeDate(dateValue: any, t?: (key: string) => string): string {
  try {
    let date: Date

    // Handle different date formats (same logic as formatDate)
    if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
      date = dateValue.toDate()
    } else if (dateValue instanceof Date) {
      date = dateValue
    } else if (typeof dateValue === 'string') {
      date = new Date(dateValue)
    } else if (typeof dateValue === 'number') {
      date = new Date(dateValue)
    } else {
      return t?.('common.unknown') || 'Unknown'
    }

    if (isNaN(date.getTime())) {
      return t?.('common.invalidDate') || 'Invalid date'
    }

    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return 'Today'
    } else if (diffDays === 1) {
      return 'Yesterday'
    } else if (diffDays < 7) {
      return `${diffDays} days ago`
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7)
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30)
      return `${months} month${months > 1 ? 's' : ''} ago`
    } else {
      const years = Math.floor(diffDays / 365)
      return `${years} year${years > 1 ? 's' : ''} ago`
    }
  } catch (error) {
    console.error('Error formatting relative date:', error, dateValue)
    return t?.('common.unknown') || 'Unknown'
  }
} 